// FILE: components/sections/FormsSection.tsx
// This section documents the styles for form elements like inputs and checkboxes.

import React, {useState} from 'react';
import {
    SectionContainer,
    ComponentPreview,
    SubSection,
    Input,
    Checkbox,
    Radio,
    Textarea,
    ToggleSwitch,
    PropsTable,
    PasswordInput,
} from '../Content';
import { Select, SelectOption } from '../Select';
import { Icon } from '../icons';
import CodeBlock from '../CodeBlock';
import { Breadcrumb } from '../Breadcrumb';
import { PropDef } from '../../../types';

const inputProps: PropDef[] = [
    { name: 'label', type: 'string', default: 'N/A', description: 'The text label displayed above the input.' },
    { name: 'id', type: 'string', default: 'N/A', description: 'A unique ID for the input, used to link the label.' },
    { name: 'error', type: 'string', default: 'undefined', description: 'If present, displays an error message below the input.' },
    { name: 'className', type: 'string', default: '""', description: 'Optional classes for the input element.' },
];

const passwordInputProps: PropDef[] = [
    { name: 'id', type: 'string', default: 'N/A', description: 'A unique ID for the input, used to link the label.' },
    { name: 'label', type: 'string', default: 'undefined', description: 'The text label displayed above the input.' },
    { name: 'value', type: 'string', default: 'N/A', description: 'The current value of the password input.' },
    { name: 'onChange', type: '(value: string) => void', default: 'N/A', description: 'Callback function when the password value changes.' },
    { name: 'placeholder', type: 'string', default: '"Enter password"', description: 'Placeholder text shown when the input is empty.' },
    { name: 'disabled', type: 'boolean', default: 'false', description: 'If true, the input is disabled and cannot be interacted with.' },
    { name: 'error', type: 'string', default: '""', description: 'If present, displays an error message below the input.' },
    { name: 'className', type: 'string', default: '""', description: 'Optional classes for the input container.' },
    { name: 'autoComplete', type: 'string', default: '"current-password"', description: 'HTML autocomplete attribute for the password field.' },
    { name: 'required', type: 'boolean', default: 'false', description: 'If true, adds a required indicator (*) to the label.' },
];
const selectOptions: SelectOption[] = [
    { value: '', label: 'Select a category' },
    { value: 'books', label: 'Book Fulfillment' },
    { value: 'media', label: 'Media Manufacturing' },
    { value: 'ecommerce', label: 'E-Commerce Shipping' },
];
const checkboxRadioProps: PropDef[] = [
    { name: 'label', type: 'string', default: 'N/A', description: 'The text label displayed next to the control.' },
    { name: 'id', type: 'string', default: 'N/A', description: 'A unique ID, used to link the label.' },
];
const selectProps: PropDef[] = [
    { name: 'label', type: 'string', default: 'N/A', description: 'The text label displayed above the select dropdown.' },
    { name: 'id', type: 'string', default: 'N/A', description: 'A unique ID, used to link the label.' },
    { name: 'options', type: '{value: string; label: string;}[]', default: '[]', description: 'An array of option objects for the dropdown.' },
    { name: 'value', type: 'string | number', default: 'N/A', description: 'The current value of the select component.' },
    { name: 'onChange', type: '(value: string | number) => void', default: 'N/A', description: 'Callback function when a new option is selected.' },
    { name: 'error', type: 'string', default: 'undefined', description: 'If present, displays an error message below the select.' },
];

const formsCode = `import { Input, Textarea, Checkbox, Radio, ToggleSwitch, PasswordInput } from './Content';
import { Select } from './Select';

const [selectValue, setSelectValue] = useState('');
const [password, setPassword] = useState('');
const options = [{ value: 'books', label: 'Books' }];

<Select 
    label="Category" 
    id="category" 
    options={options} 
    value={selectValue}
    onChange={(value) => setSelectValue(String(value))}
/>

<Input label="Name" id="name" type="text" placeholder="John Doe" />

<PasswordInput
    label="Password"
    id="password"
    value={password}
    onChange={setPassword}
    placeholder="Enter your password"
    required
/>

<Textarea label="Your Message" id="message" rows={4} />

<Checkbox label="I agree" id="terms" />

<Radio label="Option 1" id="radio1" name="options" />

<ToggleSwitch label="Enable notifications" id="notifications" />`;

export const FormsSection: React.FC<{ groupTitle: string; sectionTitle:string; }> = ({ groupTitle, sectionTitle }) => {
    const [radioValue, setRadioValue] = useState('selected');
    const [selectValue, setSelectValue] = useState('');
    const [demoPassword, setDemoPassword] = useState('');
    const [demoConfirmPassword, setDemoConfirmPassword] = useState('');
    const [demoRequiredPassword, setDemoRequiredPassword] = useState('');
    const [demoDisabledPassword, setDemoDisabledPassword] = useState('hidden123');
    
    return (
        <SectionContainer id="forms">
            <Breadcrumb group={groupTitle} section={sectionTitle} />
            <h2 className="section-title">Forms & Inputs</h2>
            <p className="section-subtitle">
                Clear, consistent, and accessible form controls are vital for a good user experience. Our forms are encapsulated into reusable components to be intuitive and provide clear feedback for all interaction states.
            </p>
            
            <SubSection title="Text Inputs & Textareas">
              <ComponentPreview className="flex-col !items-start gap-8">
                <Input
                    label="Full Name"
                    id="default-input"
                    type="text"
                    placeholder="e.g., Jane Doe"
                />
                <Textarea
                    label="Your Message"
                    id="default-textarea"
                    placeholder="Let us know how we can help..."
                    rows={4}
                />
                <Input
                    label="Email Address (Error)"
                    id="error-input"
                    type="email"
                    defaultValue="invalid-email"
                    error="Please enter a valid email address."
                />
                <Input
                    label="Company Name (Disabled)"
                    id="disabled-input"
                    type="text"
                    placeholder="Acutrack"
                    disabled
                />
              </ComponentPreview>
            </SubSection>

            <SubSection title="Password Inputs">
                <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    Specialized password inputs with built-in visibility toggle for better user experience and security.
                </p>
                
                <div className="bg-system-info/10 border border-system-info/20 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <Icon className="!text-xl text-system-info mt-0.5 shrink-0">lightbulb</Icon>
                        <div>
                            <h4 className="font-bold text-system-info mb-1">💡 Pro Tip</h4>
                            <p className="text-sm text-system-info/90">
                                Always use <code className="bg-white/20 px-1.5 py-0.5 rounded text-xs font-mono">PasswordInput</code> instead of <code className="bg-white/20 px-1.5 py-0.5 rounded text-xs font-mono">{'<input type="password" />'}</code> to get the built-in visibility toggle and consistent design system styling.
                            </p>
                        </div>
                    </div>
                </div>

                <ComponentPreview className="flex-col !items-start gap-8">
                    <PasswordInput
                        label="Password"
                        id="password-demo"
                        value={demoPassword}
                        onChange={setDemoPassword}
                        placeholder="Enter your password"
                    />
                    <PasswordInput
                        label="Confirm Password"
                        id="confirm-password-demo"
                        value={demoConfirmPassword}
                        onChange={setDemoConfirmPassword}
                        placeholder="Confirm your password"
                        error={demoPassword && demoConfirmPassword && demoPassword !== demoConfirmPassword ? "Passwords don't match" : ""}
                    />
                    <PasswordInput
                        label="Required Password"
                        id="required-password-demo"
                        value={demoRequiredPassword}
                        onChange={setDemoRequiredPassword}
                        placeholder="Enter required password"
                        required
                    />
                    <PasswordInput
                        label="Disabled Password"
                        id="disabled-password-demo"
                        value={demoDisabledPassword}
                        onChange={setDemoDisabledPassword}
                        disabled
                    />
                </ComponentPreview>
            </SubSection>
            
            <SubSection title="Native vs. PasswordInput Comparison">
                <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    See the difference between using native HTML password inputs and our enhanced PasswordInput component.
                </p>
                
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4 flex items-center gap-2">
                            <Icon className="!text-lg text-system-error">cancel</Icon>
                            Native HTML Input
                        </h4>
                        <div className="space-y-4">
                            <input 
                                type="password" 
                                placeholder="Enter password" 
                                className="w-full h-14 px-4 bg-bg-tertiary dark:bg-dark-bg-primary border border-border-color dark:border-dark-border-color rounded-lg focus:outline-none focus:ring-0 focus:border-2 focus:border-brand-navy dark:focus:border-dark-brand-orange text-text-primary dark:text-dark-text-primary"
                            />
                            <div className="text-sm text-text-secondary dark:text-dark-text-secondary space-y-2">
                                <p>❌ No visibility toggle</p>
                                <p>❌ Basic browser styling</p>
                                <p>❌ No error handling</p>
                                <p>❌ Inconsistent with design system</p>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4 flex items-center gap-2">
                            <Icon className="!text-lg text-system-success">check_circle</Icon>
                            Our PasswordInput Component
                        </h4>
                        <div className="space-y-4">
                            <PasswordInput
                                id="comparison-demo"
                                value=""
                                onChange={() => {}}
                                placeholder="Enter password"
                            />
                            <div className="text-sm text-text-secondary dark:text-dark-text-secondary space-y-2">
                                <p>✅ Built-in visibility toggle</p>
                                <p>✅ Consistent design system styling</p>
                                <p>✅ Built-in error handling</p>
                                <p>✅ Professional appearance</p>
                            </div>
                        </div>
                    </div>
                </div>
            </SubSection>
            
            <SubSection title="PasswordInput Demo Code">
                <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    Here are practical examples of how to implement PasswordInput in your projects.
                </p>
                
                <div className="space-y-6">
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">Basic Usage</h4>
                        <CodeBlock code={`import { PasswordInput } from '@acutrack-bookprint/acutrack-ds';

const [password, setPassword] = useState('');

<PasswordInput
    label="Password"
    id="password"
    value={password}
    onChange={setPassword}
    placeholder="Enter your password"
/>`} />
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">With Error Handling</h4>
                        <CodeBlock code={`const [password, setPassword] = useState('');
const [error, setError] = useState('');

const validatePassword = (value: string) => {
    if (value.length < 8) {
        setError('Password must be at least 8 characters');
    } else {
        setError('');
    }
};

<PasswordInput
    label="Password"
    id="password"
    value={password}
    onChange={(value) => {
        setPassword(value);
        validatePassword(value);
    }}
    error={error}
    required
/>`} />
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">Form Integration</h4>
                        <CodeBlock code={`const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
});

const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
        alert('Passwords do not match!');
        return;
    }
    // Submit form logic
};

<form onSubmit={handleSubmit} className="space-y-4">
    <Input
        label="Email"
        id="email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({...formData, email: e.target.value})}
    />
    <PasswordInput
        label="Password"
        id="password"
        value={formData.password}
        onChange={(value) => setFormData({...formData, password: value})}
        required
    />
    <PasswordInput
        label="Confirm Password"
        id="confirmPassword"
        value={formData.confirmPassword}
        onChange={(value) => setFormData({...formData, confirmPassword: value})}
        error={formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword ? "Passwords don't match" : ""}
        required
    />
    <Button type="submit">Create Account</Button>
</form>`} />
                    </div>
                </div>
            </SubSection>
            
            <SubSection title="Select Dropdown">
                <ComponentPreview className="flex-col !items-start gap-8">
                    <Select 
                        label="Service Category" 
                        id="select-default" 
                        options={selectOptions} 
                        value={selectValue}
                        onChange={(value) => setSelectValue(String(value))}
                    />
                    <Select 
                        label="Service Category (Disabled)" 
                        id="select-disabled" 
                        options={selectOptions} 
                        value={selectValue}
                        onChange={(value) => setSelectValue(String(value))}
                        disabled 
                    />
                </ComponentPreview>
            </SubSection>

            <SubSection title="Selection Controls">
                <ComponentPreview>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12 w-full">
                        <div className="space-y-6">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Checkboxes</h4>
                            <Checkbox label="Default" id="check-default" />
                            <Checkbox label="Checked" id="check-checked" defaultChecked />
                            <Checkbox label="Disabled" id="check-disabled" disabled />
                        </div>
                        <div className="space-y-6">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Radio Buttons</h4>
                             <Radio label="Default" id="radio-default" name="plan" value="default" checked={radioValue === 'default'} onChange={(e) => setRadioValue(e.target.value)} />
                            <Radio label="Selected" id="radio-selected" name="plan" value="selected" checked={radioValue === 'selected'} onChange={(e) => setRadioValue(e.target.value)} />
                            <Radio label="Disabled" id="radio-disabled" name="plan-disabled" disabled />
                        </div>
                        <div className="space-y-6">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Toggle Switches</h4>
                             <ToggleSwitch label="Notifications" id="toggle-default" />
                            <ToggleSwitch label="Marketing emails" id="toggle-checked" defaultChecked />
                            <ToggleSwitch label="Sync data" id="toggle-disabled" disabled />
                        </div>
                    </div>
                </ComponentPreview>
            </SubSection>
            
            <SubSection title="Usage Guidelines">
                <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <Icon className="!text-2xl text-system-success mt-1 shrink-0">check_circle</Icon>
                            <div>
                                <h4 className="font-bold text-text-primary dark:text-dark-text-primary">Always Use Labels</h4>
                                <p className="text-sm">Every form control must have a corresponding `{'<label>'}`. This is critical for screen reader accessibility.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Icon className="!text-2xl text-system-success mt-1 shrink-0">check_circle</Icon>
                            <div>
                                <h4 className="font-bold text-text-primary dark:text-dark-text-primary">Provide Helpful Error Messages</h4>
                                <p className="text-sm">When validation fails, clearly state what is wrong and how to fix it (e.g., "Password must be at least 8 characters long").</p>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <Icon className="!text-2xl text-system-error mt-1 shrink-0">cancel</Icon>
                            <div>
                                <h4 className="font-bold text-text-primary dark:text-dark-text-primary">Don't Use Placeholders as Labels</h4>
                                <p className="text-sm">Placeholder text disappears on input, which forces users to rely on memory. Use them only as hints or examples (e.g., "jane.doe@example.com").</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Icon className="!text-2xl text-system-error mt-1 shrink-0">cancel</Icon>
                            <div>
                                <h4 className="font-bold text-text-primary dark:text-dark-text-primary">Avoid Long Select Lists</h4>
                                <p className="text-sm">For lists with more than 15 options, consider an autocomplete input instead of a `{'<select>'}` to improve usability.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </SubSection>

            <SubSection title="Component Props">
                <div className="space-y-12">
                     <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<Input /> & <Textarea />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={inputProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<PasswordInput />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={passwordInputProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<Select />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={selectProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<Checkbox />, <Radio />, & <ToggleSwitch />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={checkboxRadioProps} /></div>
                    </div>
                </div>
            </SubSection>

            <SubSection title="Implementation Example">
                <CodeBlock code={formsCode} />
            </SubSection>

        </SectionContainer>
    );
}